import { SetStateAction, createContext, useEffect, useState } from "react";
import { api } from "../api/api";
import { useLocation } from "react-router-dom";

interface iPostProvider {
  children: React.ReactNode;
}

type iUser = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  username: string;
  icon: string;
  role: "user" | "admin";
  phone: string;
  email: string;
  password: string;
  deletedAt: Date | null;
  bio?: string;
  image?: string;
};

export type iPostUserImageLikeSchema = {
  count: number;
  raw: {
    id: number;
    message: string;
    like: number;
    dislike: number;
    comment: number;
    Images: {
      id: number;
      createdAt: Date;
      publicId: string;
      url: string;
      secureUrl: string;
    }[];
    User: iUser;
    createdAt: Date;
    updatedAt: Date;
  }[];
};

type iCommentUserNoUserId = {
  count: number;
  raw: {
    id: number;
    message: string;
    like: number;
    dislike: number;
    createdAt: Date;
    updatedAt: Date;
    User: {
      id: number;
      createdAt: Date;
      updatedAt: Date;
      name: string;
      username: string;
      icon: string;
      role: "user" | "admin";
      image?: string;
    };
    postId: number;
  }[];
};

type iUsersWithoutPass = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  username: string;
  icon: string;
  role: "user" | "admin";
  phone: string;
  email: string;
  deletedAt: Date | null;
  bio?: string;
  image?: string;
};

export type iPost = {
  id: number;
  message: string;
  createdAt: Date;
  updatedAt: Date;
  like: number;
  dislike: number;
  comment: number;
  Images: {
    id: number;
    createdAt: Date;
    publicId: string;
    url: string;
    secureUrl: string;
  }[];
  User: iUsersWithoutPass;
};

type iCreatePostData = {
  images: File[];
  message: string;
};

interface iPostContext {
  loadPosts: (page: number) => Promise<iPostUserImageLikeSchema | void>;
  posts: iPostUserImageLikeSchema | null;
  setPosts: React.Dispatch<SetStateAction<iPostUserImageLikeSchema | null>>;
  loading: boolean;
  comments: iCommentUserNoUserId | null;
  createPostComment: (
    postId: number | null,
    message: string | null,
    page: number
  ) => void;
  getPostComments: (
    postId: number | null,
    page: number
  ) => Promise<iCommentUserNoUserId | void>;
  setLoading: React.Dispatch<SetStateAction<boolean>>;
  likePost: (id: number, type: "like" | "dislike") => void;
  likeComment: (
    id: number,
    type: "like" | "dislike",
    postId: number | null,
    page: number
  ) => void;
  getUserPosts: (
    userId: number | null,
    page: number
  ) => Promise<iPostUserImageLikeSchema | void>;
  postsUser: iPostUserImageLikeSchema | null;
  setPostsUser: React.Dispatch<SetStateAction<iPostUserImageLikeSchema | null>>;
  createPost: (createPostData: iCreatePostData) => void;
  getUserFollowingPosts: (
    id: number
  ) => Promise<iPostUserImageLikeSchema | void>;
  getPostsAllAdmin: (page: number) => Promise<iPostUserImageLikeSchema | void>;
  getPostActivity: (page: number) => Promise<iPostUserImageLikeSchema | void>;
  getTypeLikePost: (id: number) => Promise<string | void>;
  getTypeLikeComment: (id: number) => Promise<string | void>;
}

export const PostContext = createContext({} as iPostContext);

export const PostProvider = ({ children }: iPostProvider) => {
  const [posts, setPosts] = useState<iPostUserImageLikeSchema | null>(null),
    [comments, setComments] = useState<iCommentUserNoUserId | null>(null),
    [loading, setLoading] = useState(true),
    [postsUser, setPostsUser] = useState<iPostUserImageLikeSchema | null>(null);

  const loadPosts = async (
    page = 1
  ): Promise<iPostUserImageLikeSchema | void> => {
    try {
      const response = await api.get(`/posts?page=${page}&limit=10`);
      if (page === 1) {
        setPosts(response.data);
      } else {
        setPosts((prevPosts) => {
          const updatedPosts = prevPosts ? prevPosts : { count: 0, raw: [] };
          return {
            raw: [...updatedPosts.raw, ...response.data.raw],
            count: updatedPosts.count + response.data.raw.length,
          };
        });
      }
    } catch (error) {
      console.error("Erro ao carregar posts:", error);
    }
  };

  const createPost = async (createPostData: iCreatePostData) => {
    try {
      const responseCreatePost = await api.post("/posts", {
        message: createPostData.message,
      });

      if (createPostData.images) {
        const formData = new FormData();
        createPostData.images.forEach((image) => {
          formData.append("images", image);
        }); // Adiciona o arquivo ao FormData

        await api.post("/upload/" + responseCreatePost.data.id, formData, {
          headers: {
            "Content-Type": "multipart/form-data", // Define o tipo de conteúdo como multipart/form-data
          },
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      loadPosts();
    }
  };

  const getPostComments = async (
    postId: number | null,
    page: number
  ): Promise<iCommentUserNoUserId | void> => {
    try {
      const response = await api.get(
        `comment/all/${postId}?page=${page}&limit=10`
      );

      // Verifica se é a primeira página
      if (page === 1) {
        setComments(response.data); // Define os comentários como a resposta da primeira página
      } else {
        setComments((prevComments) => {
          const updatedComments = prevComments
            ? prevComments
            : { count: 0, raw: [] };

          return {
            raw: [...updatedComments.raw, ...response.data.raw],
            count: updatedComments.count + response.data.raw.length,
          };
        });
      }

      return response.data;
    } catch (error) {
      console.log(error);
    }
  };

  const createPostComment = async (
    postId: number | null,
    message: string | null,
    page: number
  ) => {
    try {
      await api.post(`/comment/${postId}`, { message });
    } catch (error) {
      console.log(error);
    } finally {
      await getPostComments(postId, page);
    }
  };

  const likePost = async (id: number, type: "like" | "dislike") => {
    try {
      await api.post(`posts/like/${id}`, { type });
    } catch (error) {
      console.log(error);
    } finally {
      loadPosts();
    }
  };

  const likeComment = async (
    id: number,
    type: "like" | "dislike",
    postId: number | null,
    page: number
  ) => {
    try {
      await api.post(`comment/like/${id}`, { type });
    } catch (error) {
      console.log(error);
    } finally {
      await getPostComments(postId, page);
    }
  };

  const getUserPosts = async (
    userId: number | null,
    page = 1
  ): Promise<iPostUserImageLikeSchema | void> => {
    try {
      const response = await api.get(
        `posts/user/${userId}?page=${page}&limit=20`
      );
      if (page === 1) {
        setPostsUser(response.data); // Define os posts como a resposta da primeira página
      } else {
        setPostsUser((prevPosts) => {
          const updatedPosts = prevPosts ? prevPosts : { count: 0, raw: [] };

          return {
            raw: [...updatedPosts.raw, ...response.data.raw],
            count: updatedPosts.count + response.data.raw.length,
          };
        });
      }

      return response.data;
    } catch (error) {
      console.log("Erro ao buscar posts:", error);
    }
  };

  const getUserFollowingPosts = async (
    id: number
  ): Promise<iPostUserImageLikeSchema | void> => {
    try {
      const response = await api.get(`posts/user/follow/${id}`);

      setPosts(response.data);

      return response.data;
    } catch (error) {
      console.log("Erro ao buscar posts:", error);
    }
  };

  const getPostsAllAdmin = async (
    page = 1
  ): Promise<iPostUserImageLikeSchema | void> => {
    try {
      const response = await api.get(`/posts/admin?page=${page}&limit=20`);
      if (page === 1) {
        setPosts(response.data);
      } else {
        setPosts((prevPosts) => {
          const updatedPosts = prevPosts ? prevPosts : { count: 0, raw: [] };
          return {
            raw: [...updatedPosts.raw, ...response.data.raw],
            count: updatedPosts.count + response.data.raw.length,
          };
        });
      }
    } catch (error) {
      console.error("Erro ao carregar posts:", error);
    }
  };

  const getPostActivity = async (
    page = 1
  ): Promise<iPostUserImageLikeSchema | void> => {
    try {
      const response = await api.get(`/posts/activity?page=${page}&limit=20`);
      if (page === 1) {
        setPosts(response.data);
      } else {
        setPosts((prevPosts) => {
          const updatedPosts = prevPosts ? prevPosts : { count: 0, raw: [] };
          return {
            raw: [...updatedPosts.raw, ...response.data.raw],
            count: updatedPosts.count + response.data.raw.length,
          };
        });
      }
    } catch (error) {
      console.error("Erro ao carregar os post com mais atividade: ", error);
    }
  };

  const getTypeLikePost = async (id: number): Promise<string | void> => {
    try {
      const response = await api.get(`/posts/liketype/${id}`);

      return response.data.type;
    } catch (error) {
      console.error("Erro ao buscar o tipo de interação no post", error);
    }
  };

  const getTypeLikeComment = async (id: number): Promise<string | void> => {
    try {
      const response = await api.get(`/comment/liketype/${id}`);

      return response.data.type;
    } catch (error) {
      console.error("Erro ao buscar o tipo de interação no comentário", error);
    }
  };

  return (
    <PostContext.Provider
      value={{
        posts,
        setPosts,
        loading,
        setLoading,
        comments,
        createPostComment,
        getPostComments,
        likePost,
        likeComment,
        getUserPosts,
        postsUser,
        setPostsUser,
        loadPosts,
        createPost,
        getUserFollowingPosts,
        getPostsAllAdmin,
        getPostActivity,
        getTypeLikePost,
        getTypeLikeComment,
      }}
    >
      {children}
    </PostContext.Provider>
  );
};
